import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdKeyboardArrowUp, MdKeyboardArrowDown, MdBusiness, MdCheckCircle } from 'react-icons/md';

interface CollapsibleCardProps {
  title: string;
  description: string;
  relevanceToBusiness: string;
  actionableInsights: string;
  icon?: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  showImage?: boolean;
}

const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  description,
  relevanceToBusiness,
  actionableInsights,
  icon,
  isExpanded,
  onToggle,
  showImage = true
}) => {

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "linear-gradient(90deg,rgba(33,33,33,0.92),rgba(55,55,55,0.92))",
        border: '1px solid rgba(224, 224, 224, 0.13)'
      }}
    >
      {/* Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full p-6 flex cursor-pointer items-center justify-between hover:bg-black/20 transition-colors"
      >
        <div className="flex items-center gap-4">
          {/* {icon && (
            <div className="w-12 h-12 rounded-lg bg-brand-primary flex items-center justify-center text-white flex-shrink-0">
              {icon}
            </div>
          )} */}
          <h3 className="text-lg font-normal text-brand-primary text-left">
            {title}
          </h3>
        </div>
        <div className="text-white text-3xl flex-shrink-0">
          {isExpanded ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
        </div>
      </button>

      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pt-4 pb-6 space-y-6 bg-[#000000]">
              {/* Description with Optional Image */}
              <div className="flex gap-6">
                {showImage && (
                  <div className="w-auto h-40 flex-shrink-0 bg-gray-700 rounded-lg overflow-hidden">
                    <img
                      src="/assets/images/diverse-community.jpeg"
                      alt="Demographic"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x200/4a5568/ffffff?text=Demographics';
                      }}
                    />
                  </div>
                )}
                <p className="text-white leading-relaxed flex-1 font-light text-sm">
                  {description}
                </p>
              </div>

              {/* Business Relevance & Actionable Insights */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Business Relevance */}
                <div className="bg-surface-dark rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <MdBusiness className="text-white" size={18} />
                    </div>
                    <h4 className="text-md font-medium text-white uppercase tracking-wid">
                      Business Relevance
                    </h4>
                  </div>
                  <p className="text-gray-300 text-[14px] leading-relaxed">
                    {relevanceToBusiness}
                  </p>
                </div>

                {/* Actionable Insights */}
                <div className="bg-surface-dark rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <MdCheckCircle className="text-white" size={18} />
                    </div>
                    <h4 className="text-md font-medium text-white uppercase tracking-wid">
                      Actionable Insights
                    </h4>
                  </div>
                  <p className="text-gray-300 text-[14px] leading-relaxed">
                    {actionableInsights}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleCard;
